import React, { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import moment from 'moment';
import toast from 'react-hot-toast';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../../components/CustomSingleSelect';
import CustomMultiSelect, { CustomMultiSelectModel } from '../../../components/CustomMultiSelect';
import CustomDatePicker from '../../../components/CustomDatePicker';
import CustomTextInput from '../../../components/CustomTextInput';
import {
    bolumSec, bolumSecenekleri, kademeSecimiOf, KademeSecimi, musteriSec, musteriSecenekleri, poNoSecenekleri, poSec, poSecenekleri
} from '../../../helpers/poKademe';
import { depoPoSatirlari, depoVarsayilanKademe, varsayilanDepoId } from '../../../helpers/mesaiDepoEslesme';
import { saatAraligiCoz, saatAraligiEtiketi, saatAraligiHatasi, saatAraligiMetni, talepBitisGenis, talepBitisSaati } from '../../../helpers/saatAraligi';
import TalepPersonelDurumlari from './TalepPersonelDurumlari';
import { davetDurumu, personelSayiHatasi, personelZorunluMu } from '../../../helpers/mesaiDavet';
import { MesaiTalebiItem, MesaiTalebiKaydetIstegi, MesaiTalebiSecenekleri } from '../../../services/MesaiTalebiService';

interface Props {
    isShown: boolean
    /** Güncellenecek talep; yoksa yeni talep. */
    kayit: MesaiTalebiItem | undefined
    secenekler: MesaiTalebiSecenekleri
    onConfirm: (_istek: MesaiTalebiKaydetIstegi) => void
    onClose: () => void
}

const listeOf = (l: CustomSingleSelectModel[]) => () => Promise.resolve({ isSuccess: true, data: l });
const secim = (l: CustomSingleSelectModel[], v: string | undefined) => v ? l.find(x => x.value == v) : undefined;

/**
 * Ek Mesai Talepleri > Ekle / Güncelle penceresi (MesaiTalebi/Kaydet, QR Mesai Faz 2, 18.10.2026).
 * Müşteri → Bölüm → PO → PO No kademeli (Atanacak Mesailer'deki seçimle aynı); saat aralığı Çalışma Saatleri'ndeki listeden.
 */
const MesaiTalebiModal = ({ isShown, kayit, secenekler, onConfirm, onClose }: Props) => {
    const [tarih, setTarih] = useState<string | undefined>(moment().format('YYYY-MM-DD'));
    const [depoId, setDepoId] = useState<string | undefined>(undefined);
    const [kademe, setKademe] = useState<KademeSecimi>({});
    const [aralik, setAralik] = useState<string | undefined>(undefined);
    const [kisi, setKisi] = useState<string>('1');
    const [ayrilanlar, setAyrilanlar] = useState<CustomMultiSelectModel[]>([]);
    const [aciklama, setAciklama] = useState<string | undefined>('');

    const tumPoSatirlari = secenekler.poSatirlari ?? [];
    // #507: seçili deponun müşteri / bölüm eşleşmesi varsa yalnız eşleşen PO satırları.
    const poSatirlari = depoPoSatirlari(tumPoSatirlari, (secenekler.depolar ?? []).find(x => x.mesaiNoktasiId == depoId));

    useEffect(() => {
        if (!isShown)
            return;

        if (kayit) {
            setTarih(kayit.tarih.substring(0, 10));
            setDepoId(kayit.mesaiNoktasiId);
            setKademe(kademeSecimiOf(tumPoSatirlari, kayit.companyPoId));
            setAralik(saatAraligiMetni(kayit.baslangic, talepBitisSaati(kayit.bitis)));
            setKisi(String(kayit.kisiSayisi));
            setAyrilanlar((kayit.ayrilanlar ?? []).map(x => ({ label: x.ad, value: x.personnelId })));
            setAciklama(kayit.aciklama ?? '');
        } else {
            setTarih(moment().format('YYYY-MM-DD'));
            // #507: tek depoda yetkili kullanıcıda o depo; depo eşleşmesinde tek müşteri / bölüm kendiliğinden gelir.
            const depo = varsayilanDepoId(secenekler.depolar ?? [], secenekler.varsayilanDepoId);
            setDepoId(depo);
            setKademe(depo ? depoVarsayilanKademe(depoPoSatirlari(tumPoSatirlari, (secenekler.depolar ?? []).find(x => x.mesaiNoktasiId == depo))) : {});
            setAralik(undefined);
            setKisi('1');
            setAyrilanlar([]);
            setAciklama('');
        }
    }, [isShown])

    // Seçenekler pencere açıldıktan sonra gelirse güncellenen talebin PO zinciri yeniden kurulur.
    useEffect(() => {
        if (isShown && kayit && !kademe.musteri && !kademe.companyPoId)
            setKademe(kademeSecimiOf(tumPoSatirlari, kayit.companyPoId));
    }, [secenekler])

    // ---------------------------------------------------------------- Seçenekler

    const depoSecenekleri: CustomSingleSelectModel[] = (secenekler.depolar ?? []).map(x => ({ label: x.ad, value: x.mesaiNoktasiId }));

    // Kayıtlı talebin deposu artık listede yoksa (pasif / ofise çevrilmiş) yine de görünsün.
    if (kayit && !depoSecenekleri.some(x => x.value == kayit.mesaiNoktasiId))
        depoSecenekleri.push({ label: kayit.depoAdi, value: kayit.mesaiNoktasiId });

    const aralikSecenekleri: CustomSingleSelectModel[] = (secenekler.araliklar ?? [])
        .map(saatAraligiCoz)
        .filter(x => x != null)
        .map(x => ({ label: saatAraligiEtiketi(x!.baslangic, x!.bitis), value: saatAraligiMetni(x!.baslangic, x!.bitis) }));

    // Talebin aralığı sonradan listeden çıkarılmış olabilir.
    if (aralik && !aralikSecenekleri.some(x => x.value == aralik)) {
        var c = saatAraligiCoz(aralik);
        if (c) aralikSecenekleri.push({ label: saatAraligiEtiketi(c.baslangic, c.bitis), value: aralik });
    }

    // PO satırı listede yoksa (PO kaldırılmış) güncellemede eski etiketle gösterilir.
    const poEksik = kayit != undefined && kademe.musteri == undefined && kademe.companyPoId == undefined
        && !!kayit.companyPoId && !tumPoSatirlari.some(x => x.companyPoId == kayit.companyPoId);

    const mS = musteriSecenekleri(poSatirlari), bS = bolumSecenekleri(poSatirlari, kademe), pS = poSecenekleri(poSatirlari, kademe), nS = poNoSecenekleri(poSatirlari, kademe);

    // Depo değişince müşteri / bölüm / PO yeni deponun eşleşmesine göre sıfırlanır (tek seçenek kendiliğinden gelir).
    const depoDegisti = (id: string | undefined) => {
        setDepoId(id);
        setKademe(id ? depoVarsayilanKademe(depoPoSatirlari(tumPoSatirlari, (secenekler.depolar ?? []).find(x => x.mesaiNoktasiId == id))) : {});
    }

    // #514: reddeden kişi bu talepte yeniden seçilemez.
    const reddedenler = kayit?.reddedenler ?? [];
    const personelSecenekleri = () => Promise.resolve({
        isSuccess: true,
        data: (secenekler.personeller ?? []).filter(x => !reddedenler.some(r => r.personnelId == x.personnelId)).map(x => ({ label: x.ad, value: x.personnelId }))
    });

    const secilenAralik = aralik ? saatAraligiCoz(aralik) : null;
    const personelZorunlu = personelZorunluMu(!kayit, kayit?.gecmisDavet, tarih, secilenAralik ? talepBitisGenis(secilenAralik.baslangic, secilenAralik.bitis) : undefined);
    const davetSatirlari = [...(kayit?.ayrilanlar ?? []), ...reddedenler].filter(x => x.davetDurumu != null);

    // ---------------------------------------------------------------- Kaydet

    const kaydet = () => {
        if (!tarih) {
            toast.error('Tarih alanı zorunludur.');
            return;
        }

        if (!depoId) {
            toast.error('Depo seçiniz.');
            return;
        }

        var companyPoId = kademe.companyPoId ?? (poEksik ? kayit!.companyPoId : undefined);

        if (!companyPoId) {
            toast.error('Müşteri, Bölüm, PO ve PO No seçiniz.');
            return;
        }

        var c = saatAraligiCoz(aralik);

        if (!c) {
            toast.error('Mesai saat aralığı seçiniz.');
            return;
        }

        var aralikHatasi = saatAraligiHatasi(c.baslangic, c.bitis);

        if (aralikHatasi) {
            toast.error(aralikHatasi);
            return;
        }

        var kisiSayisi = Number(kisi);

        if (!Number.isInteger(kisiSayisi) || kisiSayisi < 1 || kisiSayisi > 99) {
            toast.error('Kişi sayısı 1 ile 99 arasında tam sayı olmalıdır.');
            return;
        }

        var personelHatasi = personelSayiHatasi(personelZorunluMu(!kayit, kayit?.gecmisDavet, tarih, talepBitisGenis(c.baslangic, c.bitis)), ayrilanlar.length, kisiSayisi);

        if (personelHatasi) {
            toast.error(personelHatasi);
            return;
        }

        onConfirm({
            mesaiTalebiId: kayit?.mesaiTalebiId ?? null,
            tarih: tarih,
            mesaiNoktasiId: depoId,
            companyPoId: companyPoId,
            kisiSayisi: kisiSayisi,
            baslangic: c.baslangic,
            bitis: c.bitis,
            ayrilanPersonelIdleri: ayrilanlar.map(x => x.value),
            aciklama: aciklama && aciklama.trim() != '' ? aciklama.trim() : null
        });
    }

    return (
        <Modal
            tabIndex={-1}
            dialogClassName='modal-dialog modal-dialog-centered mw-800px'
            show={isShown}
            onHide={onClose}
            backdrop={'static'}
            keyboard={false}
        >
            <div className='modal-header'>
                <h2 className='mb-0'>{kayit ? 'Ek Mesai Talebini Güncelle' : 'Yeni Ek Mesai Talebi'}</h2>
            </div>

            <div className='modal-body'>
                <div className='row mb-5'>
                    <div className='col-md-4'>
                        <CustomDatePicker labelText='Tarih' isLabelRequired value={tarih} setValue={setTarih} />
                    </div>

                    <div className='col-md-8'>
                        <CustomSingleSelect key={'depo-' + (kayit?.mesaiTalebiId ?? 'yeni')} labelText='Depo' isLabelRequired selectedValue={secim(depoSecenekleri, depoId)} onChange={(v) => depoDegisti(v?.value)} onMenuOpen={listeOf(depoSecenekleri)} labelName='label' valueName='value' />
                    </div>
                </div>

                <div className='row mb-5'>
                    <div className='col-md-6 mb-5'>
                        <CustomSingleSelect labelText='Müşteri' isLabelRequired selectedValue={secim(mS, kademe.musteri)} onChange={(v) => setKademe(musteriSec(poSatirlari, kademe, v?.value))} onMenuOpen={listeOf(mS)} labelName='label' valueName='value' />
                    </div>
                    <div className='col-md-6 mb-5'>
                        <CustomSingleSelect labelText='Bölüm' isLabelRequired disabled={!kademe.musteri} selectedValue={secim(bS, kademe.bolum)} onChange={(v) => setKademe(bolumSec(poSatirlari, kademe, v?.value))} onMenuOpen={listeOf(bS)} labelName='label' valueName='value' />
                    </div>
                    <div className='col-md-6'>
                        <CustomSingleSelect labelText='PO' isLabelRequired disabled={!kademe.bolum} selectedValue={secim(pS, kademe.po)} onChange={(v) => setKademe(poSec(poSatirlari, kademe, v?.value))} onMenuOpen={listeOf(pS)} labelName='label' valueName='value' />
                    </div>
                    <div className='col-md-6'>
                        <CustomSingleSelect labelText='PO No' isLabelRequired disabled={!kademe.po} selectedValue={secim(nS, kademe.companyPoId)} onChange={(v) => setKademe({ ...kademe, companyPoId: v?.value })} onMenuOpen={listeOf(nS)} labelName='label' valueName='value' />
                    </div>
                    {
                        poEksik && (
                            <div className='col-md-12 mt-2 text-warning fs-7'>
                                Kayıtlı PO ({kayit!.poEtiketi}) artık PO Eşleştirme listesinde yok; değiştirmezseniz olduğu gibi kalır.
                            </div>
                        )
                    }
                </div>

                <div className='row mb-5'>
                    <div className='col-md-6'>
                        <CustomSingleSelect labelText='Mesai saat aralığı' isLabelRequired selectedValue={secim(aralikSecenekleri, aralik)} onChange={(v) => setAralik(v?.value)} onMenuOpen={listeOf(aralikSecenekleri)} labelName='label' valueName='value' />
                    </div>

                    <div className='col-md-6'>
                        <label className="form-label required">Kişi sayısı</label>
                        <input
                            type="number"
                            step="1"
                            min="1"
                            max="99"
                            className="form-control form-control-solid"
                            style={{ border: '1px solid #F1F1F4' }}
                            value={kisi}
                            onChange={(e) => setKisi(e.target.value)}
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <CustomMultiSelect
                        labelText={personelZorunlu ? `Personel (${kisi || 0} kişi seçiniz, seçilen: ${ayrilanlar.length})` : 'Personel (davetsiz eski talepte isteğe bağlı)'}
                        isLabelRequired={personelZorunlu}
                        selectedValues={ayrilanlar}
                        onChange={setAyrilanlar}
                        onMenuOpen={personelSecenekleri}
                        labelName='label'
                        valueName='value'
                    />
                </div>

                <TalepPersonelDurumlari tarih={tarih} aralik={secilenAralik} personelIdleri={ayrilanlar.map(x => x.value)} />

                {
                    kayit?.revizeNotu && (
                        <div className='alert alert-danger py-3 mb-5'>
                            <b>Revize istendi:</b> {kayit.revizeNotu}
                            <div className='fs-8 mt-1'>Düzeltip kaydedince talep yeniden onaya gider.</div>
                        </div>
                    )
                }

                {
                    davetSatirlari.length > 0 && (
                        <div className='mb-5'>
                            <label className='form-label'>Davet durumu</label>
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-sm align-middle gs-0 gy-2 mb-0 fs-7'>
                                    <thead>
                                        <tr className='text-muted fw-bold'>
                                            <th>Personel</th><th>Durum</th><th>Davet</th><th>Yanıt</th><th>Süre</th><th>Not</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            davetSatirlari.map(x => {
                                                const d = davetDurumu(x.davetDurumu);
                                                return (
                                                    <tr key={x.personnelId}>
                                                        <td>
                                                            {x.ad}
                                                            {x.telefonVar === false && <div className='text-warning fs-8'>Telefonu kayıtlı değil, bildirim gitmedi</div>}
                                                        </td>
                                                        <td><span className={'badge ebbe-status-badge ' + d.sinif}>{d.metin}</span></td>
                                                        <td>{x.davetZamani}</td>
                                                        <td>{x.yanitZamani}</td>
                                                        <td>{x.yanitSuresi}</td>
                                                        <td style={{ whiteSpace: 'normal' }}>{x.redNotu}</td>
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }

                {
                    (kayit?.onayGecmisi?.length ?? 0) > 0 && (
                        <div className='mb-5'>
                            <label className='form-label'>Onay tarihçesi</label>
                            <div className='table-responsive'>
                                <table className='table table-row-dashed align-middle fs-7 mb-0'>
                                    <thead>
                                        <tr className='text-muted'>
                                            <th>Tarih</th>
                                            <th>Kullanıcı</th>
                                            <th>İşlem</th>
                                            <th>Açıklama</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            kayit!.onayGecmisi!.map((x, i) => (
                                                <tr key={i}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{x.tarih}</td>
                                                    <td>{x.kullanici}</td>
                                                    <td>{x.islemAdi}</td>
                                                    <td style={{ whiteSpace: 'normal' }}>{x.aciklama}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }

                <div className='mb-5'>
                    <CustomTextInput labelText='Açıklama' value={aciklama} setValue={setAciklama} />
                </div>

                <div className='alert alert-primary d-flex align-items-start p-4 mb-0'>
                    <i className='fa fa-info-circle text-primary fs-3 me-3 mt-1'></i>
                    <div className='fs-7'>
                        Seçilen personele mobil uygulamadan ek mesai daveti gider; yalnız yeni eklenen kişiye gider, kayıt yenilenince tekrar gitmez.
                        {' '}Davetli kişi QR okutunca talebe yerleşir; davet edilmeyen kişinin mesaisi Atanacak Mesailer'e düşer.
                        {' '}Faturalanan, aralığa düşen ek mesaidir (15 dakikaya aşağı yuvarlanır); ilk kişi yerleşince talebin siparişi açılır.
                        {' '}Bitişi başlangıçtan önce olan aralık ertesi gün biter (22:00–02:00 (+1)). Vardiyalı personelde vardiya saatine denk gelen kısım faturalanmaz.
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <button onClick={onClose} type="button" className="btn btn-light">
                    Vazgeç
                </button>
                <button onClick={kaydet} type="button" className="btn btn-success">
                    Kaydet
                </button>
            </div>
        </Modal>
    )
}

export default MesaiTalebiModal;
